import { iconSetaBaixo } from '@bsds-lib-component/icons/icons';
import { BsdsIcon } from '@bsds-lib-component/react';
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';

import type { DropdownOption, DropdownProps } from '../../types/componentType';


const Dropdown: React.FC<DropdownProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Selecione',
  disabled = false,
  label,
  errorText,
  id,
  'data-testid': dataTestId,
  required = false,
  touched = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState<string>('');
  const [internalTouched, setInternalTouched] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const listboxRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const generatedId = useId();
  const dropdownId = id || generatedId;
  const listboxId = `${dropdownId}-listbox`;
  const errorId = `${dropdownId}-error`;
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);

  // Add "Selecione" option automatically at the beginning
  const SELECT_OPTION: DropdownOption = useMemo(() => ({
    label: placeholder,
    value: '',
    disabled: false,
  }), [placeholder]);

  const allOptions = useMemo(() => [SELECT_OPTION, ...options], [SELECT_OPTION, options]);

  // Check if current value is empty or the select option
  const isEmpty = !value || value === '';

  // Determine if should show error
  const isTouched = touched || internalTouched;
  const shouldShowError = required && isEmpty && isTouched;
  const hasError = !!errorText || shouldShowError;
  const errorMessage = errorText || (shouldShowError ? 'Este campo é obrigatório' : undefined);

  // Closure to handle clicks outside the dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update selected label when value changes
  useEffect(() => {
    const allOptionsWithSelect = [SELECT_OPTION, ...options];
    const selected = allOptionsWithSelect.find(opt => opt.value === value);
    setSelectedLabel(selected?.label || placeholder);
  }, [value, options, placeholder, SELECT_OPTION]);

  const handleSelect = (option: DropdownOption) => {
    if (!option.disabled) {
      onChange(option.value);
      setIsOpen(false);
      setFocusedIndex(-1);
      setInternalTouched(true);
      buttonRef.current?.focus();
    }
  };

  const handleBlur = () => {
    setInternalTouched(true);
  };

  const handleButtonClick = () => {
    if (!disabled) {
      setInternalTouched(true);
      setIsOpen(!isOpen);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;

    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else if (focusedIndex >= 0 && options[focusedIndex] && !options[focusedIndex].disabled) {
          handleSelect(options[focusedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setFocusedIndex(-1);
        buttonRef.current?.focus();
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          const nextIndex = findNextEnabledIndex(focusedIndex, 1);
          setFocusedIndex(nextIndex);
          scrollToOption(nextIndex);
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (isOpen) {
          const prevIndex = findNextEnabledIndex(focusedIndex, -1);
          setFocusedIndex(prevIndex);
          scrollToOption(prevIndex);
        }
        break;
      case 'Home':
        e.preventDefault();
        if (isOpen) {
          const firstIndex = findNextEnabledIndex(-1, 1);
          setFocusedIndex(firstIndex);
          scrollToOption(firstIndex);
        }
        break;
      case 'End':
        e.preventDefault();
        if (isOpen) {
          const lastIndex = findNextEnabledIndex(options.length, -1);
          setFocusedIndex(lastIndex);
          scrollToOption(lastIndex);
        }
        break;
    }
  };

  const findNextEnabledIndex = (currentIndex: number, direction: number): number => {
    let nextIndex = currentIndex + direction;
    while (nextIndex >= 0 && nextIndex < allOptions.length) {
      if (!allOptions[nextIndex].disabled) {
        return nextIndex;
      }
      nextIndex += direction;
    }
    return currentIndex;
  };

  const scrollToOption = (index: number) => {
    if (listboxRef.current && index >= 0 && index < allOptions.length) {
      const optionElement = listboxRef.current.children[index] as HTMLElement;
      if (optionElement) {
        optionElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  };

  // Reset focused index when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setFocusedIndex(-1);
    } else if (isOpen && focusedIndex === -1 && allOptions.length > 0) {
      // Focus first enabled option when opening
      const firstEnabled = findNextEnabledIndex(-1, 1);
      if (firstEnabled >= 0) {
        setFocusedIndex(firstEnabled);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, allOptions]);

  const displayText = value && value !== '' ? selectedLabel : placeholder;

  return (
    <div className={`flex flex-col w-full`} ref={containerRef}>
      {label && (
        <label htmlFor={id} className="block text-md mb-[0.5px] font-bold text-gray-800">
          {label}
        </label>
      )}

      {/* Input Field can click */}
      <div className="relative">
        <button
          ref={buttonRef}
          type="button"
          id={dropdownId}
          disabled={disabled}
          onClick={handleButtonClick}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className={`w-full h-14 px-4 text-left text-sm font-medium rounded-lg transition-all duration-200 flex items-center justify-between ${disabled
            ? 'bg-gray-50 text-gray-400 cursor-not-allowed border border-gray-300 shadow-sm'
            : 'text-gray-700 cursor-pointer'
            } ${hasError || isOpen
              ? 'border-t-[0.5px] border-l-[0.5px] border-r-[0.5px] border-b-[0.5px] border-t-red-600 border-l-red-600 border-r-red-600 border-b-red-600 shadow-[0_1px_3px_0_rgba(0,0,0,0.05),0_3px_0_0_rgb(220,38,38)] after:content-[""] after:absolute after:bottom-[-3px] after:left-[8px] after:right-[8px] after:h-[3px] after:bg-red-600 after:rounded-b-lg'
              : 'border border-gray-300 hover:border-gray-400 shadow-sm focus:outline-none focus:ring-[0.1px] focus:ring-red-600 focus:border-red-600'
            } relative`.trim()}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-label={label ? `${label}: ${displayText}` : displayText}
          aria-describedby={errorMessage ? errorId : undefined}
          aria-disabled={disabled}
          data-testid={dataTestId}
        >
          <span className={value && value !== '' ? 'text-text-color' : 'text-gray-500'}>
            {displayText}
          </span>
          <div className="bg-secondary-600 rounded-full p-2 flex items-center justify-center">
            <BsdsIcon
              icon={iconSetaBaixo}
              className={`w-2 h-2 text-white transition-transform duration-200 ${isOpen ? 'rotate-180' : ''} ${disabled ? 'opacity-50' : ''}`}
              aria-hidden="true"
            />
          </div>
        </button>

        {/* List drop */}
        {isOpen && !disabled && (
          <div
            ref={listboxRef}
            id={listboxId}
            className={` absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto custom-scrollbar focus:outline-none animate-in fade-in slide-in-from-top-3 duration-100`}
            role="listbox"
            aria-labelledby={label ? dropdownId : undefined}
            aria-label={!label ? 'Lista de opções' : undefined}
          >
            {allOptions.length === 0 ? (
              <div
                className="px-4 py-3 text-sm text-gray-500 text-center"
                role="status"
                aria-live="polite"
              >
                Nenhuma opção disponível
              </div>
            ) : (
              allOptions.map((option, index) => {
                const isSelected = option.value === value;
                const isDisabled = option.disabled;
                const isFocused = index === focusedIndex;

                return (
                  <button
                    key={`${option.value}-${index}`}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    aria-disabled={isDisabled}
                    disabled={isDisabled}
                    onClick={() => handleSelect(option)}
                    onMouseEnter={() => !isDisabled && setFocusedIndex(index)}
                    className={` w-full px-4 py-3 text-left text-sm transition-colors ${isSelected
                      ? 'bg-red-50 text-red-700 font-medium'
                      : 'text-gray-700 hover:bg-gray-50'
                      }
                      ${isFocused && !isSelected
                        ? 'bg-gray-100'
                        : ''
                      }
                      ${isDisabled
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'cursor-pointer'
                      }
                    `}
                    aria-label={option.label}
                  >
                    {option.label}
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>

      {/* Error message */}
      {errorMessage && (
        <span
          id={errorId}
          className="mt-1 text-sm text-red-600"
          role="alert"
          aria-live="polite"
        >
          {errorMessage}
        </span>
      )}
    </div>
  );
};

export default Dropdown;